import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import teachingLogicGraphUrl from '../../../../教学逻辑图谱.png';

const chainFlow = [
  { label: '课堂场景', sub: '沙漠公路', icon: 'ri-landscape-line' },
  { label: '问题', sub: '"为什么要投入"', icon: 'ri-question-answer-line' },
  { label: '民生投入', icon: 'ri-funds-line' },
  { label: '共同富裕', icon: 'ri-scales-line' },
  { label: '以人民为中心', icon: 'ri-heart-line' },
  { label: '制度优势', icon: 'ri-shield-star-line' },
  { label: '证据卡 + 可迁移方法', icon: 'ri-file-text-line' },
];

export default function P40CaseDesertRoad({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 md:py-6">
        <div ref={titleRef} className="text-center mb-4 md:mb-6">
          <div className={`flex items-center justify-center gap-2 mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100/60 text-accent-700 text-base font-body font-semibold">
              <i className="ri-file-text-line text-base" /> 完整闭环案例 A
            </span>
          </div>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">"沙漠公路"</span>到教学逻辑图谱
          </h2>
        </div>

        {/* 完整链路 — 横向双行居中布局 */}
        <div className={`max-w-6xl mx-auto mb-6 md:mb-8 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-col items-center gap-8 md:gap-10">
            {/* 第一行：4个 */}
            <div className="flex items-center gap-2 md:gap-5">
              {chainFlow.slice(0, 4).map((step, i) => (
                <div key={step.label} className="flex items-center gap-2 md:gap-5">
                  <div className={`flex flex-col items-center text-center gap-3 px-6 py-5 md:px-8 md:py-7 rounded-2xl min-w-[150px] md:min-w-[200px] transition-all duration-300 hover:scale-105 ${i % 2 === 0 ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'}`}>
                    <i className={`${step.icon} text-5xl md:text-6xl ${i % 2 === 0 ? 'text-primary-500' : 'text-accent-500'}`} />
                    <p className="text-2xl md:text-3xl font-body font-bold text-foreground-950 whitespace-nowrap">{step.label}</p>
                    {step.sub && <p className="text-lg md:text-xl text-foreground-950 mt-1">{step.sub}</p>}
                  </div>
                  {i < 3 && (
                    <i className="ri-arrow-right-s-line text-foreground-950 text-3xl md:text-4xl flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>

            {/* 向下箭头 */}
            <div className="flex items-center justify-center">
              <i className="ri-arrow-down-line text-primary-500 text-3xl md:text-4xl" />
            </div>

            {/* 第二行：3个 */}
            <div className="flex items-center gap-2 md:gap-5">
              {chainFlow.slice(4).map((step, i) => (
                <div key={step.label} className="flex items-center gap-2 md:gap-5">
                  <div className={`flex flex-col items-center text-center gap-3 px-6 py-5 md:px-8 md:py-7 rounded-2xl min-w-[150px] md:min-w-[200px] transition-all duration-300 hover:scale-105 ${(i + 4) % 2 === 0 ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'}`}>
                    <i className={`${step.icon} text-5xl md:text-6xl ${(i + 4) % 2 === 0 ? 'text-primary-500' : 'text-accent-500'}`} />
                    <p className="text-2xl md:text-3xl font-body font-bold text-foreground-950 whitespace-nowrap">{step.label}</p>
                    {step.sub && <p className="text-lg md:text-xl text-foreground-950 mt-1">{step.sub}</p>}
                  </div>
                  {i < 2 && (
                    <i className="ri-arrow-right-s-line text-foreground-950 text-3xl md:text-4xl flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 底部总结 */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <button
            type="button"
            className="inline-flex items-center gap-3 px-5 py-3 rounded-xl glass border-white/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
            onClick={() => window.open(teachingLogicGraphUrl, '_blank', 'noopener,noreferrer')}
          >
            <i className="ri-loop-left-line text-primary-500 text-xl md:text-2xl flex-shrink-0" />
            <span className="text-base md:text-lg font-body text-foreground-950 leading-relaxed">
              技术过程变成可理解的教学过程：看见→判断→服务教研
            </span>
            <i className="ri-external-link-line text-primary-500 text-lg md:text-xl flex-shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
}
